import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Crown, 
  Heart, 
  BookOpen, 
  Calendar, 
  Users,
  ArrowRight,
  Sparkles,
  CheckCircle2,
  Flame
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { user } = useAuth();
  const [declaration, setDeclaration] = useState(null);
  const [noContact, setNoContact] = useState(null);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [declRes, trackerRes, modulesRes] = await Promise.all([
        axios.get(`${API}/declaration/today`),
        axios.get(`${API}/tracker/no-contact`),
        axios.get(`${API}/modules`)
      ]);
      setDeclaration(declRes.data);
      setNoContact(trackerRes.data);
      setModules(modulesRes.data);
    } catch (error) {
      console.error('Dashboard error:', error);
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    { icon: Crown, label: 'Adjust My Crown', path: '/adjust-crown', color: 'from-amber-400 to-amber-600', description: 'Need support right now?' },
    { icon: Heart, label: 'No-Contact Tracker', path: '/tracker', color: 'from-rose-400 to-rose-600', description: `${noContact?.days || 0} days strong` },
    { icon: BookOpen, label: 'Healing Modules', path: '/modules', color: 'from-purple-400 to-purple-600', description: 'Continue learning' },
    { icon: Users, label: 'Sisterhood', path: '/prayer-wall', color: 'from-pink-400 to-pink-600', description: 'Connect with sisters' },
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading your dashboard...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 lg:p-10 max-w-6xl mx-auto" data-testid="dashboard-page">
        {/* Welcome Header */}
        <div className="mb-10">
          <h1 className="font-serif text-3xl lg:text-4xl font-bold text-foreground mb-2">
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-muted-foreground">
            You're doing the hard work. Keep going, Queen.
          </p>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <div className="crown-card p-4 lg:p-6">
            <div className="flex items-center gap-3 mb-2">
              <Flame className="w-5 h-5 text-orange-500" />
              <span className="text-sm text-muted-foreground">No-Contact Streak</span>
            </div>
            <p className="text-3xl font-bold text-foreground">{noContact?.days || 0}</p>
            <p className="text-xs text-muted-foreground">days strong</p>
          </div>
          
          <div className="crown-card p-4 lg:p-6">
            <div className="flex items-center gap-3 mb-2">
              <Heart className="w-5 h-5 text-rose-500" />
              <span className="text-sm text-muted-foreground">Urges Resisted</span>
            </div>
            <p className="text-3xl font-bold text-foreground">{noContact?.urges_resisted || 0}</p>
            <p className="text-xs text-muted-foreground">victories</p>
          </div>
          
          <div className="crown-card p-4 lg:p-6">
            <div className="flex items-center gap-3 mb-2">
              <BookOpen className="w-5 h-5 text-purple-500" />
              <span className="text-sm text-muted-foreground">Modules Progress</span>
            </div>
            <p className="text-3xl font-bold text-foreground">
              {modules.reduce((acc, m) => acc + m.completed_lessons, 0)}
            </p>
            <p className="text-xs text-muted-foreground">lessons completed</p>
          </div>
          
          <div className="crown-card p-4 lg:p-6">
            <div className="flex items-center gap-3 mb-2">
              {user?.is_premium ? (
                <Sparkles className="w-5 h-5 text-amber-500" />
              ) : (
                <Crown className="w-5 h-5 text-amber-500" />
              )}
              <span className="text-sm text-muted-foreground">Status</span>
            </div>
            <p className="text-lg font-bold text-foreground">
              {user?.is_premium ? 'Premium' : 'Free Tier'}
            </p>
            {!user?.is_premium && (
              <Link to="/subscription" className="text-xs text-primary hover:underline">
                Upgrade →
              </Link>
            )}
          </div>
        </div>

        {/* Daily Declaration */}
        {declaration && (
          <div className="glass-gold rounded-2xl p-6 lg:p-8 mb-10 animate-fade-in" data-testid="daily-declaration">
            <div className="flex items-center gap-3 mb-4">
              <Calendar className="w-6 h-6 text-amber-600" />
              <span className="text-sm font-medium text-amber-700 dark:text-amber-300">Today's Crown Declaration</span>
            </div>
            <p className="font-serif text-xl lg:text-2xl font-semibold text-foreground mb-3">
              "{declaration.declaration}"
            </p>
            <p className="font-scripture text-base italic text-muted-foreground">
              {declaration.scripture}
            </p>
            <Link to="/declaration">
              <Button variant="outline" className="mt-4 rounded-full border-amber-400/50 text-amber-700 hover:bg-amber-50 dark:text-amber-300 dark:hover:bg-amber-900/20">
                See All Declarations
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        )}

        {/* Quick Actions */}
        <div className="mb-10">
          <h2 className="font-serif text-2xl font-semibold text-foreground mb-6">Quick Actions</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {quickActions.map((action, idx) => {
              const Icon = action.icon;
              return (
                <Link 
                  key={action.path} 
                  to={action.path}
                  data-testid={`quick-action-${idx}`}
                >
                  <div className="crown-card p-5 hover:border-primary/30 transition-all duration-300 hover:-translate-y-1 group">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${action.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="font-semibold text-foreground mb-1">{action.label}</h3>
                    <p className="text-sm text-muted-foreground">{action.description}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Healing Modules Progress */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-serif text-2xl font-semibold text-foreground">Healing Modules</h2>
            <Link to="/modules" className="text-sm text-primary hover:underline flex items-center gap-1">
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {modules.map((module, idx) => (
              <Link 
                key={module.id} 
                to={`/modules/${module.id}`}
                data-testid={`module-card-${idx}`}
              >
                <div className="crown-card p-5 hover:border-primary/30 transition-all duration-300">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-foreground">{module.title}</h3>
                    {module.progress === 1 && (
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">{module.description}</p>
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">{module.completed_lessons} of {module.total_lessons} lessons</span>
                      <span className="font-medium text-primary">{Math.round(module.progress * 100)}%</span>
                    </div>
                    <Progress value={module.progress * 100} className="h-2" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}
